import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { priceBookAPI, importsAPI, crmImportAPI } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { Layout } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Card, CardContent } from '../components/ui/card';
import { AppDrawer } from '../components/ui/AppDrawer';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import { SortableTableHead } from '../components/ui/SortableTableHead';
import { useSortableTable } from '../hooks/useSortableTable';
import {
  Tooltip, TooltipContent, TooltipProvider, TooltipTrigger,
} from '../components/ui/tooltip';
import {
  Plus, Search, Loader2, BookOpen, Edit2, Trash2, FolderOpen, DollarSign,
  Download, Upload, FileSpreadsheet, AlertCircle, Wrench, Package, Layers,
  Clock, Thermometer, Droplets, Zap, DoorOpen, LayoutGrid, Camera, X,
  ChevronDown, ChevronRight,
} from 'lucide-react';
import { toast } from 'sonner';

import StarterPacks from '../components/StarterPacks';
import PriceBookImport from '../components/PriceBookImport';
import HCPPricebookView from '../components/HCPPricebookView';
import { ImageThumbnail } from '../components/ImageThumbnail';
import { MobileFABContainer, MobileFABMenu, MobileFABMenuItem } from '../components/MobileFAB';

const TYPE_COLORS = {
  service: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
  material: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400',
  labor: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400',
  bundle: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
};

const UNIT_OPTIONS = [
  { value: 'each', label: 'Each' }, { value: 'hour', label: 'Hour' },
  { value: 'job', label: 'Per Job' }, { value: 'ft', label: 'Foot (ft)' },
  { value: 'sqft', label: 'Sq Foot (sqft)' }, { value: 'lb', label: 'Pound (lb)' },
  { value: 'unit', label: 'Unit' },
];

const TABS = [
  { key: 'services', label: 'Services', icon: Wrench, type: 'service' },
  { key: 'materials', label: 'Materials', icon: Package, type: 'material' },
  { key: 'templates', label: 'Templates', icon: Layers, type: 'bundle' },
];

const INDUSTRY_ICON_MAP = { Thermometer, Droplets, Zap, DoorOpen };

const IND_COLORS = {
  hvac: { bg: 'bg-orange-500/10', border: 'border-orange-300 dark:border-orange-700', icon: 'text-orange-600 dark:text-orange-400', active: 'border-orange-400 dark:border-orange-500 bg-orange-500/15 shadow-sm shadow-orange-200/50 dark:shadow-orange-900/30' },
  plumbing: { bg: 'bg-blue-500/10', border: 'border-blue-300 dark:border-blue-700', icon: 'text-blue-600 dark:text-blue-400', active: 'border-blue-400 dark:border-blue-500 bg-blue-500/15 shadow-sm shadow-blue-200/50 dark:shadow-blue-900/30' },
  electrical: { bg: 'bg-yellow-500/10', border: 'border-yellow-300 dark:border-yellow-700', icon: 'text-yellow-600 dark:text-yellow-400', active: 'border-yellow-400 dark:border-yellow-500 bg-yellow-500/15 shadow-sm shadow-yellow-200/50 dark:shadow-yellow-900/30' },
  garage_doors: { bg: 'bg-slate-500/10', border: 'border-slate-300 dark:border-slate-600', icon: 'text-slate-600 dark:text-slate-400', active: 'border-slate-400 dark:border-slate-500 bg-slate-500/15 shadow-sm shadow-slate-200/50 dark:shadow-slate-900/30' },
};
const DEFAULT_IND_COLORS = { bg: 'bg-violet-500/10', border: 'border-violet-300 dark:border-violet-700', icon: 'text-violet-600 dark:text-violet-400', active: 'border-violet-400 dark:border-violet-500 bg-violet-500/15 shadow-sm shadow-violet-200/50 dark:shadow-violet-900/30' };

const COLOR_OPTIONS = [
  { value: 'hvac', label: 'Orange', dot: 'bg-orange-500' },
  { value: 'plumbing', label: 'Blue', dot: 'bg-blue-500' },
  { value: 'electrical', label: 'Yellow', dot: 'bg-yellow-500' },
  { value: 'garage_doors', label: 'Slate', dot: 'bg-slate-500' },
];

export default function PriceBook() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { isOwner, isOffice, hasEntitlement } = useAuth();
  const canEdit = isOwner || isOffice;
  const hasCRM = hasEntitlement('crm');

  const importParam = searchParams.get('import');
  const autoOpenImport = !!importParam;
  const preSelectSource = (importParam && importParam !== '1') ? importParam : null;

  const [categories, setCategories] = useState([]);
  const [industries, setIndustries] = useState([]);
  const [unassignedCounts, setUnassignedCounts] = useState({ category_count: 0, item_count: 0 });
  const [items, setItems] = useState([]);
  const PRICEBOOK_SORT_COLUMNS = [
    { key: 'name', type: 'text' },
    { key: 'unit', type: 'text' },
    { key: 'base_cost', type: 'currency' },
    { key: 'final_price', type: 'currency' },
  ];
  const { sorted: sortedItems, sortKey: pbSortKey, sortDir: pbSortDir, onSort: pbOnSort } = useSortableTable(items, { columns: PRICEBOOK_SORT_COLUMNS });
  const [templates, setTemplates] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [catDrawerOpen, setCatDrawerOpen] = useState(false);
  const [itemDrawerOpen, setItemDrawerOpen] = useState(false);
  const [indDrawerOpen, setIndDrawerOpen] = useState(false);
  const [savingCat, setSavingCat] = useState(false);
  const [savingItem, setSavingItem] = useState(false);
  const [savingInd, setSavingInd] = useState(false);
  const [editingCat, setEditingCat] = useState(null);
  const [editingInd, setEditingInd] = useState(null);
  const [catsLoaded, setCatsLoaded] = useState(false);

  const activeTab = searchParams.get('tab') || 'services';
  const activeCat = searchParams.get('category') || '';
  const activeIndustry = searchParams.get('industry') || '';
  const search = searchParams.get('search') || '';

  const [catForm, setCatForm] = useState({ name: '', description: '', industry_id: '' });
  const [catImageFile, setCatImageFile] = useState(null);
  const [catImagePreview, setCatImagePreview] = useState(null);
  const [uploadingCatImage, setUploadingCatImage] = useState(false);

  const [indForm, setIndForm] = useState({ name: '', color_key: '' });
  const [indImageFile, setIndImageFile] = useState(null);
  const [indImagePreview, setIndImagePreview] = useState(null);
  const [uploadingIndImage, setUploadingIndImage] = useState(false);

  const [itemForm, setItemForm] = useState({ name: '', item_type: 'service', pricing_mode: 'fixed', final_price: 0, description: '', sku: '', task_code: '', taxable: true, unit: 'each', default_duration_minutes: '', base_cost: '' });
  const [defaultLaborRate, setDefaultLaborRate] = useState(null);
  const [laborRateInput, setLaborRateInput] = useState('');
  const [fabOpen, setFabOpen] = useState(false);
  const [savingRate, setSavingRate] = useState(false);

  const [importDrawerOpen, setImportDrawerOpen] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [exporting, setExporting] = useState(false);
  const fileInputRef = useRef(null);
  const [showMigrationHint, setShowMigrationHint] = useState(false);
  const [bulkAdjustOpen, setBulkAdjustOpen] = useState(false);

  const tabType = TABS.find(t => t.key === activeTab)?.type || 'service';

  // ── Data Fetching ──────────────────────────────────────────────────
  const fetchIndustries = useCallback(async () => {
    try {
      const res = await priceBookAPI.listIndustries();
      setIndustries(res.data.industries || []);
      setUnassignedCounts(res.data.unassigned || { category_count: 0, item_count: 0 });
    } catch {}
  }, []);

  const fetchCategories = useCallback(async () => {
    try {
      const params = {};
      if (activeIndustry && activeIndustry !== 'unassigned') params.industry_id = activeIndustry;
      else if (activeIndustry === 'unassigned') params.industry_id = 'unassigned';
      const res = await priceBookAPI.listCategories(params);
      setCategories(res.data.categories || []);
    } catch {}
    finally { setCatsLoaded(true); }
  }, [activeIndustry]);

  const fetchSettings = useCallback(async () => {
    try { const res = await priceBookAPI.getSettings(); setDefaultLaborRate(res.data.default_labor_rate); setLaborRateInput(res.data.default_labor_rate != null ? String(res.data.default_labor_rate) : ''); } catch {}
  }, []);

  const fetchItems = useCallback(async () => {
    if (!hasCRM) return;
    if (activeTab === 'templates') return;
    setLoading(true);
    try {
      const params = { page_size: 100, item_type: tabType };
      if (activeCat && activeCat !== 'uncategorized') params.category_id = activeCat;
      if (activeCat === 'uncategorized') params.category_id = 'uncategorized';
      if (!activeCat && activeIndustry) params.industry_id = activeIndustry;
      if (search) params.search = search;
      const res = await priceBookAPI.listItems(params);
      setItems(res.data.items || []); setTotal(res.data.total || 0);
    } catch { toast.error('Failed to load items'); }
    finally { setLoading(false); }
  }, [hasCRM, activeCat, activeIndustry, search, tabType, activeTab]);

  const fetchTemplates = useCallback(async () => {
    if (!hasCRM) return;
    if (activeTab !== 'templates') return;
    setLoading(true);
    try {
      const res = await priceBookAPI.listTemplates({ search: search || undefined });
      setTemplates(res.data.templates || []); setTotal(res.data.total || 0);
    } catch {}
    finally { setLoading(false); }
  }, [hasCRM, activeTab, search]);

  useEffect(() => { if (hasCRM) { fetchIndustries(); fetchCategories(); fetchSettings(); } }, [hasCRM, fetchIndustries, fetchCategories, fetchSettings]);
  useEffect(() => { fetchItems(); }, [fetchItems]);
  useEffect(() => { fetchTemplates(); }, [fetchTemplates]);

  useEffect(() => {
    if (!hasCRM) return;
    const check = async () => {
      try {
        const res = await priceBookAPI.listItems({ page_size: 1 });
        if ((res.data.total || 0) < 5) {
          const sessions = await crmImportAPI.sessions();
          if (sessions.data?.sessions?.length > 0) setShowMigrationHint(true);
        }
      } catch {}
    };
    check();
  }, [hasCRM]);

  // ── URL Param Helpers ──────────────────────────────────────────────
  const setParam = (key, value) => { setSearchParams(prev => { const p = new URLSearchParams(prev); if (value) p.set(key, value); else p.delete(key); return p; }); };
  const setMultipleParams = (updates) => { setSearchParams(prev => { const p = new URLSearchParams(prev); Object.entries(updates).forEach(([k, v]) => { if (v) p.set(k, v); else p.delete(k); }); return p; }); };
  const handleIndustrySelect = (id) => { setMultipleParams({ industry: id || '', category: '', search: '' }); };

  // ── Category Handlers ──────────────────────────────────────────────
  const handleSaveCat = async (e) => {
    e.preventDefault(); setSavingCat(true);
    try {
      const payload = { name: catForm.name, description: catForm.description || undefined };
      if (catForm.industry_id) payload.industry_id = catForm.industry_id;
      let catId;
      if (editingCat) { await priceBookAPI.updateCategory(editingCat.id, payload); catId = editingCat.id; toast.success('Category updated'); }
      else { const res = await priceBookAPI.createCategory(payload); catId = res.data.id; toast.success('Category created'); }
      if (catImageFile && catId) { setUploadingCatImage(true); try { await priceBookAPI.uploadCategoryImage(catId, catImageFile); } catch { toast.error('Image upload failed'); } setUploadingCatImage(false); }
      setCatDrawerOpen(false); setEditingCat(null); setCatForm({ name: '', description: '', industry_id: '' }); setCatImageFile(null); setCatImagePreview(null);
      fetchCategories(); fetchIndustries();
    } catch { toast.error('Failed to save category'); }
    finally { setSavingCat(false); }
  };

  const handleDeleteCat = async (id) => {
    if (!window.confirm('Delete this category? Items will become uncategorized.')) return;
    try { await priceBookAPI.deleteCategory(id); toast.success('Category deleted'); if (activeCat === id) setParam('category', ''); fetchCategories(); fetchIndustries(); } catch { toast.error('Failed to delete category'); }
  };

  const handleDeleteCatImage = async (catId) => {
    try { await priceBookAPI.deleteCategoryImage(catId); toast.success('Image removed'); fetchCategories(); } catch { toast.error('Failed'); }
  };

  // ── Industry Handlers ──────────────────────────────────────────────
  const handleSaveInd = async (e) => {
    e.preventDefault(); setSavingInd(true);
    try {
      let indId;
      const payload = { name: indForm.name, color_key: indForm.color_key || undefined };
      if (editingInd) { await priceBookAPI.updateIndustry(editingInd.id, payload); indId = editingInd.id; toast.success('Industry updated'); }
      else { const res = await priceBookAPI.createIndustry(payload); indId = res.data.id; toast.success('Industry created'); }
      if (indImageFile && indId) { setUploadingIndImage(true); try { await priceBookAPI.uploadIndustryImage(indId, indImageFile); } catch { toast.error('Image upload failed'); } setUploadingIndImage(false); }
      setIndDrawerOpen(false); setEditingInd(null); setIndForm({ name: '', color_key: '' }); setIndImageFile(null); setIndImagePreview(null);
      fetchIndustries();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Failed to save industry'); }
    finally { setSavingInd(false); }
  };

  const handleDeleteInd = async (id) => {
    if (!window.confirm('Delete this industry? Categories will become unassigned.')) return;
    try { await priceBookAPI.deleteIndustry(id); toast.success('Industry deleted'); if (activeIndustry === id) setParam('industry', ''); fetchIndustries(); fetchCategories(); } catch { toast.error('Failed'); }
  };

  // ── Item Handlers ──────────────────────────────────────────────────
  const handleCreateItem = async (e) => {
    e.preventDefault(); setSavingItem(true);
    try {
      const payload = { name: itemForm.name, item_type: itemForm.item_type, pricing_mode: itemForm.item_type === 'bundle' ? 'bundle' : 'fixed', description: itemForm.description || undefined, final_price: parseFloat(itemForm.final_price) || 0, sku: itemForm.sku || undefined, task_code: itemForm.task_code || undefined, taxable: itemForm.taxable, unit: itemForm.unit, base_cost: itemForm.base_cost ? parseFloat(itemForm.base_cost) : undefined, default_duration_minutes: itemForm.default_duration_minutes ? parseInt(itemForm.default_duration_minutes) : undefined };
      if (activeCat && activeCat !== 'uncategorized') payload.category_id = activeCat;
      const res = await priceBookAPI.createItem(payload);
      toast.success(`${res.data.name} created`); setItemDrawerOpen(false); resetItemForm(); navigate(`/price-book/${res.data.id}`);
    } catch (err) { toast.error(err?.response?.data?.detail || 'Failed to create item'); }
    finally { setSavingItem(false); }
  };
  const resetItemForm = () => setItemForm({ name: '', item_type: tabType === 'bundle' ? 'service' : tabType, pricing_mode: 'fixed', final_price: 0, description: '', sku: '', task_code: '', taxable: true, unit: 'each', default_duration_minutes: '', base_cost: '' });

  const handleSaveLaborRate = async () => {
    setSavingRate(true);
    try { const val = laborRateInput.trim() === '' ? null : parseFloat(laborRateInput); const res = await priceBookAPI.updateSettings({ default_labor_rate: val }); setDefaultLaborRate(res.data.default_labor_rate); toast.success('Labor rate updated'); fetchItems(); } catch { toast.error('Failed'); }
    finally { setSavingRate(false); }
  };

  // ── Misc Handlers ──────────────────────────────────────────────────
  const downloadBlob = (blob, fn) => { const u = URL.createObjectURL(blob); const a = document.createElement('a'); a.href = u; a.download = fn; document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(u); };
  const handleExport = async (type) => { setExporting(true); try { const f = type === 'components' ? priceBookAPI.exportComponentsCsv : type === 'categories' ? priceBookAPI.exportCategoriesCsv : priceBookAPI.exportItemsCsv; const r = await f(); downloadBlob(new Blob([r.data], { type: 'text/csv' }), `price_book_${type}.csv`); toast.success(`Exported`); } catch { toast.error('Export failed'); } finally { setExporting(false); } };
  const handleImport = async (file) => { if (!file) return; setImporting(true); setImportResult(null); try { const res = await importsAPI.upload('price_book', file); toast.success(`Uploaded ${res.data.total_rows} rows`); setImportDrawerOpen(false); navigate(`/import-review/${res.data.id}`); } catch (err) { const msg = err?.response?.data?.detail || 'Import failed'; setImportResult({ ok: false, error: msg }); toast.error(msg); } finally { setImporting(false); if (fileInputRef.current) fileInputRef.current.value = ''; } };
  const handleDeleteTemplate = async (id) => { try { await priceBookAPI.deleteTemplate(id); toast.success('Template deleted'); fetchTemplates(); } catch { toast.error('Failed'); } };
  const handleRefreshAll = () => { fetchCategories(); fetchItems(); fetchTemplates(); fetchIndustries(); };

  const activeIndustryObj = industries.find(i => i.id === activeIndustry);
  const activeIndustryLabel = activeIndustry === 'unassigned' ? 'Unassigned' : activeIndustryObj?.name || null;
  const activeIndustryColorKey = activeIndustryObj?.color_key || null;

  // Group categories by industry for the "All" view
  const isAllView = !activeIndustry;
  const groupedCategories = isAllView ? groupCategoriesByIndustry(categories, industries) : null;

  // Inventory-only tenants (no CRM) — show read-only HCP Pricebook
  if (!hasCRM) {
    return <HCPPricebookView />;
  }

  return (
    <Layout>
      <div className="space-y-4" data-testid="price-book-page">
        {/* Header */}
        <div className="flex items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2" data-testid="price-book-title"><BookOpen className="w-6 h-6" /> Price Book</h1>
            <p className="text-sm text-muted-foreground mt-0.5 hidden sm:block">{total} {activeTab === 'templates' ? 'template' : 'item'}{total !== 1 ? 's' : ''}{activeIndustryLabel ? ` in ${activeIndustryLabel}` : ''}</p>
          </div>
          {canEdit && (
            <div className="hidden md:flex gap-2 flex-wrap">
              <TooltipProvider>
                <Tooltip><TooltipTrigger asChild><Button variant="outline" size="icon" onClick={() => handleExport('items')} disabled={exporting} data-testid="export-items-btn">{exporting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}</Button></TooltipTrigger><TooltipContent>Export CSV</TooltipContent></Tooltip>
              </TooltipProvider>
              <Button variant="outline" onClick={() => setBulkAdjustOpen(true)} data-testid="bulk-adjust-btn"><DollarSign className="w-4 h-4 mr-1" /> Bulk Adjust</Button>
              <PriceBookImport onImported={handleRefreshAll} autoOpen={autoOpenImport} preSelectSource={preSelectSource} />
              <Button variant="outline" onClick={() => { setEditingCat(null); setCatForm({ name: '', description: '', industry_id: activeIndustry && activeIndustry !== 'unassigned' ? activeIndustry : '' }); setCatImageFile(null); setCatImagePreview(null); setCatDrawerOpen(true); }} data-testid="add-category-btn"><FolderOpen className="w-4 h-4 mr-2" /> Category</Button>
              {activeTab !== 'templates' && <Button onClick={() => { resetItemForm(); setItemDrawerOpen(true); }} data-testid="add-item-btn"><Plus className="w-4 h-4 mr-2" /> New {activeTab === 'materials' ? 'Material' : 'Service'}</Button>}
              {activeTab === 'templates' && <Button onClick={() => navigate('/price-book/new-template')} data-testid="add-template-btn"><Plus className="w-4 h-4 mr-2" /> New Template</Button>}
            </div>
          )}
        </div>

        {/* Starter Packs */}
        <StarterPacks onInstalled={handleRefreshAll} activeIndustryColorKey={activeIndustryColorKey} />

        {/* Migration hint */}
        {showMigrationHint && (
          <div className="flex items-center gap-3 p-3 rounded-lg border border-blue-200 dark:border-blue-800 bg-blue-50/50 dark:bg-blue-950/20" data-testid="migration-hint-banner">
            <FileSpreadsheet className="w-5 h-5 text-blue-600 dark:text-blue-400 shrink-0" />
            <span className="text-sm flex-1">Already migrated? <strong>Import your Price Book</strong> for faster estimates.</span>
            <Button size="sm" variant="outline" className="shrink-0 border-blue-300 text-blue-700" onClick={() => { document.querySelector('[data-testid="import-pricebook-btn"]')?.click(); }} data-testid="migration-hint-import-btn"><Upload className="w-3.5 h-3.5 mr-1.5" /> Import</Button>
            <button onClick={() => setShowMigrationHint(false)} className="text-muted-foreground hover:text-foreground text-xs shrink-0">Dismiss</button>
          </div>
        )}

        {/* Industry Tiles */}
        {industries.length > 0 && (
          <IndustryTiles
            industries={industries}
            unassigned={unassignedCounts}
            activeIndustry={activeIndustry}
            onSelect={handleIndustrySelect}
            canEdit={canEdit}
            onEdit={(ind) => { setEditingInd(ind); setIndForm({ name: ind.name, color_key: ind.color_key || '' }); setIndImageFile(null); setIndImagePreview(ind.image_thumb_url || ind.image_url || null); setIndDrawerOpen(true); }}
            onAdd={() => { setEditingInd(null); setIndForm({ name: '', color_key: '' }); setIndImageFile(null); setIndImagePreview(null); setIndDrawerOpen(true); }}
          />
        )}

        {/* Tabs */}
        <div className="flex gap-1 border-b" data-testid="pb-tabs">
          {TABS.map(tab => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.key;
            return (
              <button key={tab.key} className={`flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${isActive ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground'}`} onClick={() => setMultipleParams({ tab: tab.key, category: '', search: '' })} data-testid={`tab-${tab.key}`}>
                <Icon className="w-4 h-4" />{tab.label}
              </button>
            );
          })}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* Left: Category Sidebar */}
          <div className="lg:col-span-1 space-y-3" data-testid="category-sidebar">
            {/* Quick filters */}
            <div className="flex gap-1.5">
              <button className={`flex-1 text-center px-2 py-1.5 rounded-md text-xs font-medium transition-all ${!activeCat ? 'bg-primary text-primary-foreground shadow-sm' : 'bg-muted/60 hover:bg-muted text-muted-foreground'}`} onClick={() => setParam('category', '')} data-testid="cat-all">All</button>
              {activeTab !== 'templates' && (
                <button className={`flex-1 text-center px-2 py-1.5 rounded-md text-xs font-medium transition-all ${activeCat === 'uncategorized' ? 'bg-primary text-primary-foreground shadow-sm' : 'bg-muted/60 hover:bg-muted text-muted-foreground'}`} onClick={() => setParam('category', 'uncategorized')} data-testid="cat-uncategorized">Uncategorized</button>
              )}
            </div>

            {/* Category list — grouped by industry when "All" */}
            {isAllView && groupedCategories ? (
              <GroupedCategorySidebar
                groups={groupedCategories}
                activeCat={activeCat}
                canEdit={canEdit}
                onSelect={(id) => setParam('category', id)}
                onEdit={(cat) => { setEditingCat(cat); setCatForm({ name: cat.name, description: cat.description || '', industry_id: cat.industry_id || '' }); setCatImageFile(null); setCatImagePreview(cat.image_thumb_url || cat.image_url || null); setCatDrawerOpen(true); }}
                onDelete={handleDeleteCat}
                onSelectIndustry={handleIndustrySelect}
                loading={!catsLoaded}
              />
            ) : (
              <div className="space-y-1">
                {!catsLoaded ? (
                  <div className="flex justify-center py-6"><Loader2 className="w-4 h-4 animate-spin text-muted-foreground" /></div>
                ) : categories.length > 0 ? categories.map(cat => (
                  <CategoryCard
                    key={cat.id} cat={cat} isActive={activeCat === cat.id} canEdit={canEdit}
                    onClick={() => setParam('category', cat.id)}
                    onEdit={() => { setEditingCat(cat); setCatForm({ name: cat.name, description: cat.description || '', industry_id: cat.industry_id || '' }); setCatImageFile(null); setCatImagePreview(cat.image_thumb_url || cat.image_url || null); setCatDrawerOpen(true); }}
                    onDelete={() => handleDeleteCat(cat.id)}
                  />
                )) : (
                  <p className="text-xs text-muted-foreground text-center py-6">{activeIndustryLabel ? `No categories in ${activeIndustryLabel}` : 'No categories yet'}</p>
                )}
              </div>
            )}

            {/* Labor rate */}
            {canEdit && activeTab !== 'templates' && (
              <div className="pt-3 border-t" data-testid="default-labor-rate-card">
                <p className="text-[11px] font-medium flex items-center gap-1.5 mb-1.5 text-muted-foreground"><DollarSign className="w-3 h-3" /> Default Labor Rate</p>
                <div className="flex items-center gap-1.5">
                  <div className="relative flex-1"><span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[10px] text-muted-foreground">$</span>
                    <Input type="number" step="0.01" min="0" className="pl-6 h-8 text-xs" value={laborRateInput} onChange={e => setLaborRateInput(e.target.value)} onKeyDown={e => e.key === 'Enter' && handleSaveLaborRate()} placeholder="0.00" data-testid="default-labor-rate-input" />
                  </div>
                  <Button size="sm" className="h-8 text-xs px-3" onClick={handleSaveLaborRate} disabled={savingRate} data-testid="save-labor-rate-btn">{savingRate ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Save'}</Button>
                </div>
              </div>
            )}
          </div>

          {/* Right: Content */}
          <div className="lg:col-span-3 space-y-4">
            <div className="relative max-w-sm">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input placeholder={`Search ${activeTab}...`} value={search} onChange={(e) => setParam('search', e.target.value)} className="pl-10" data-testid="pb-search" />
            </div>

            {/* Items Table */}
            {activeTab !== 'templates' && (
              <div className="border rounded-lg">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <SortableTableHead sortKey={pbSortKey} sortDir={pbSortDir} columnKey="name" onSort={pbOnSort}>Item</SortableTableHead>
                      <TableHead className="w-20 hidden sm:table-cell">Unit</TableHead>
                      <TableHead className="w-20 hidden md:table-cell">Taxable</TableHead>
                      <SortableTableHead sortKey={pbSortKey} sortDir={pbSortDir} columnKey="base_cost" onSort={pbOnSort} className="w-24 text-right hidden md:table-cell">Cost</SortableTableHead>
                      <SortableTableHead sortKey={pbSortKey} sortDir={pbSortDir} columnKey="final_price" onSort={pbOnSort} className="w-24 text-right">Price</SortableTableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {loading ? (
                      <TableRow><TableCell colSpan={5} className="text-center py-12"><Loader2 className="w-6 h-6 animate-spin mx-auto text-muted-foreground" /></TableCell></TableRow>
                    ) : items.length === 0 ? (
                      <TableRow><TableCell colSpan={5} className="text-center py-12">
                        <div className="flex flex-col items-center gap-2">
                          {activeTab === 'services' ? <Wrench className="w-10 h-10 text-muted-foreground/30" /> : <Package className="w-10 h-10 text-muted-foreground/30" />}
                          <p className="text-sm font-medium text-muted-foreground">No {activeTab} found</p>
                          <p className="text-xs text-muted-foreground/70">{search ? 'Try adjusting your search.' : activeIndustryLabel ? `No ${activeTab} in ${activeIndustryLabel}.` : `Create your first ${activeTab === 'services' ? 'service' : 'material'}.`}</p>
                          {canEdit && !search && <Button size="sm" className="mt-2" onClick={() => { resetItemForm(); setItemDrawerOpen(true); }}><Plus className="w-4 h-4 mr-1" /> New {activeTab === 'services' ? 'Service' : 'Material'}</Button>}
                        </div>
                      </TableCell></TableRow>
                    ) : sortedItems.map(item => (
                      <TableRow key={item.id} className="cursor-pointer hover:bg-muted/50" onClick={() => navigate(`/price-book/${item.id}`)} data-testid={`pb-item-${item.id}`}>
                        <TableCell>
                          <div className="flex items-center gap-2.5">
                            <ImageThumbnail
                              src={item.image_thumb_url || item.image_url}
                              fullSrc={item.image_url}
                              alt={item.name}
                              size="sm"
                              fallbackIcon={activeTab === 'materials' ? Package : Wrench}
                              data-testid={`pb-item-thumb-${item.id}`}
                            />
                            <div className="min-w-0">
                              <div className="flex items-center gap-2">
                                <span className="font-semibold text-sm">{item.name}</span>
                                {item.sku && <span className="text-[10px] text-muted-foreground font-mono">{item.sku}</span>}
                                {item.task_code && <Badge variant="outline" className="text-[9px]">{item.task_code}</Badge>}
                                {item.inventory_mode === 'direct' && <Badge className="bg-blue-50 text-blue-600 dark:bg-blue-950 dark:text-blue-400 text-[9px] px-1">1:1 Inv</Badge>}
                                {item.inventory_mode === 'recipe' && <Badge className="bg-amber-50 text-amber-600 dark:bg-amber-950 dark:text-amber-400 text-[9px] px-1">Recipe</Badge>}
                                {item.default_duration_minutes && <span className="text-[10px] text-muted-foreground flex items-center gap-0.5"><Clock className="w-2.5 h-2.5" />{item.default_duration_minutes}m</span>}
                              </div>
                              {item.description && <p className="text-xs text-muted-foreground mt-0.5 line-clamp-1">{item.description}</p>}
                            </div>
                          </div>
                        </TableCell>
                        <TableCell className="hidden sm:table-cell"><span className="text-xs text-muted-foreground">{item.unit || 'each'}</span></TableCell>
                        <TableCell className="hidden md:table-cell"><span className="text-xs">{item.taxable ? 'Yes' : 'No'}</span></TableCell>
                        <TableCell className="text-right hidden md:table-cell">{item.base_cost != null ? <span className="text-xs text-muted-foreground">${item.base_cost.toFixed(2)}</span> : <span className="text-xs text-muted-foreground">-</span>}</TableCell>
                        <TableCell className="text-right"><span className="text-sm font-medium">${item.calculated_price.toFixed(2)}</span></TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}

            {/* Templates Tab */}
            {activeTab === 'templates' && (
              <div className="space-y-3">
                {loading ? <div className="text-center py-12"><Loader2 className="w-6 h-6 animate-spin mx-auto text-muted-foreground" /></div>
                : templates.length === 0 ? (
                  <div className="text-center py-12"><Layers className="w-10 h-10 text-muted-foreground/30 mx-auto mb-2" /><p className="text-sm font-medium text-muted-foreground">No templates yet</p></div>
                ) : templates.map(t => (
                  <Card key={t.id} className="hover:shadow-sm transition-shadow" data-testid={`template-card-${t.id}`}>
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex items-start gap-3 min-w-0 flex-1">
                          <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-green-100 dark:bg-green-900/30 flex items-center justify-center"><Layers className="w-5 h-5 text-green-700 dark:text-green-400" /></div>
                          <div className="min-w-0">
                            <h3 className="font-semibold text-sm">{t.name}</h3>
                            {t.description && <p className="text-xs text-muted-foreground mt-0.5">{t.description}</p>}
                            <div className="flex items-center gap-3 mt-1.5">
                              <Badge variant="outline" className="text-[10px]"><Package className="w-2.5 h-2.5 mr-0.5" />{t.component_count} item{t.component_count !== 1 ? 's' : ''}</Badge>
                              <span className="text-sm font-bold">${t.template_total.toFixed(2)}</span>
                            </div>
                            {t.components.length > 0 && <div className="mt-2 space-y-0.5">{t.components.map((c, idx) => (<div key={idx} className="flex items-center gap-2 text-[11px] text-muted-foreground"><Badge className={`${TYPE_COLORS[c.item_type]} text-[9px] px-1`}>{c.item_type === 'material' ? 'MAT' : 'SVC'}</Badge><span className="truncate">{c.name}</span><span className="shrink-0">{c.quantity}x ${c.unit_price.toFixed(2)}</span></div>))}</div>}
                          </div>
                        </div>
                        {canEdit && <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive shrink-0" onClick={() => handleDeleteTemplate(t.id)} data-testid={`delete-template-${t.id}`}><Trash2 className="w-4 h-4" /></Button>}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* ── Drawers ─────────────────────────────────────────────────── */}

        {/* Category Drawer */}
        <AppDrawer open={catDrawerOpen} onOpenChange={setCatDrawerOpen} title={editingCat ? 'Edit Category' : 'New Category'} description="Organize your price book items." footer={<><Button variant="outline" onClick={() => setCatDrawerOpen(false)}>Cancel</Button><Button type="submit" form="cat-form" disabled={savingCat || uploadingCatImage} data-testid="save-cat-btn">{(savingCat || uploadingCatImage) && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}{editingCat ? 'Update' : 'Create'}</Button></>}>
          <form id="cat-form" onSubmit={handleSaveCat} className="space-y-4">
            <ImageUploadField
              currentImage={catImagePreview || (editingCat?.image_thumb_url || editingCat?.image_url)}
              onFileSelect={(file, preview) => { setCatImageFile(file); setCatImagePreview(preview); }}
              onRemove={editingCat ? () => { handleDeleteCatImage(editingCat.id); setCatImagePreview(null); } : () => { setCatImageFile(null); setCatImagePreview(null); }}
              testId="cat-image"
            />
            <div><Label>Name *</Label><Input value={catForm.name} onChange={e => setCatForm({ ...catForm, name: e.target.value })} required data-testid="cat-name-input" /></div>
            <div><Label>Description</Label><Input value={catForm.description} onChange={e => setCatForm({ ...catForm, description: e.target.value })} placeholder="Optional" data-testid="cat-desc-input" /></div>
            {industries.length > 0 && (
              <div>
                <Label>Industry</Label>
                <Select value={catForm.industry_id || '_none'} onValueChange={v => setCatForm({ ...catForm, industry_id: v === '_none' ? '' : v })}>
                  <SelectTrigger data-testid="cat-industry-select"><SelectValue placeholder="Select industry..." /></SelectTrigger>
                  <SelectContent><SelectItem value="_none">No Industry</SelectItem>{industries.map(ind => <SelectItem key={ind.id} value={ind.id}>{ind.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            )}
          </form>
        </AppDrawer>

        {/* Industry Drawer — simplified: name, optional image, optional color */}
        <AppDrawer open={indDrawerOpen} onOpenChange={setIndDrawerOpen} title={editingInd ? 'Edit Industry' : 'New Industry'} description="Group categories by trade." footer={
          <div className="flex items-center gap-2 w-full">
            {editingInd && <Button variant="outline" size="sm" className="text-destructive border-destructive/30 hover:bg-destructive/5" onClick={() => { handleDeleteInd(editingInd.id); setIndDrawerOpen(false); }} data-testid="delete-industry-btn"><Trash2 className="w-3.5 h-3.5 mr-1" /> Delete</Button>}
            <div className="flex-1" />
            <Button variant="outline" onClick={() => setIndDrawerOpen(false)}>Cancel</Button>
            <Button type="submit" form="ind-form" disabled={savingInd || uploadingIndImage} data-testid="save-industry-btn">{(savingInd || uploadingIndImage) && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}{editingInd ? 'Update' : 'Create'}</Button>
          </div>
        }>
          <form id="ind-form" onSubmit={handleSaveInd} className="space-y-4">
            <ImageUploadField
              currentImage={indImagePreview || (editingInd?.image_thumb_url || editingInd?.image_url)}
              onFileSelect={(file, preview) => { setIndImageFile(file); setIndImagePreview(preview); }}
              onRemove={editingInd ? async () => { try { await priceBookAPI.deleteIndustryImage(editingInd.id); toast.success('Image removed'); setIndImagePreview(null); fetchIndustries(); } catch { toast.error('Failed'); } } : () => { setIndImageFile(null); setIndImagePreview(null); }}
              testId="ind-image"
            />
            <div><Label>Name *</Label><Input value={indForm.name} onChange={e => setIndForm({ ...indForm, name: e.target.value })} required placeholder="e.g. HVAC, Plumbing, Roofing" data-testid="ind-name-input" /></div>
            <div>
              <Label>Color Theme <span className="text-muted-foreground font-normal">(optional)</span></Label>
              <Select value={indForm.color_key || '_none'} onValueChange={v => setIndForm({ ...indForm, color_key: v === '_none' ? '' : v })}>
                <SelectTrigger data-testid="ind-color-select"><SelectValue placeholder="Default" /></SelectTrigger>
                <SelectContent>{COLOR_OPTIONS.map(o => <SelectItem key={o.value} value={o.value}><span className="flex items-center gap-2"><span className={`w-2.5 h-2.5 rounded-full ${o.dot}`} />{o.label}</span></SelectItem>)}</SelectContent>
              </Select>
            </div>
          </form>
        </AppDrawer>

        {/* Create Item Drawer */}
        <AppDrawer open={itemDrawerOpen} onOpenChange={setItemDrawerOpen} title={`New ${itemForm.item_type === 'material' ? 'Material' : 'Service'}`} description={`Add to your price book.`} footer={<><Button variant="outline" onClick={() => setItemDrawerOpen(false)}>Cancel</Button><Button type="submit" form="item-form" disabled={savingItem} data-testid="save-item-btn">{savingItem && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} Create & Edit</Button></>}>
          <form id="item-form" onSubmit={handleCreateItem} className="space-y-4">
            <div><Label>Name *</Label><Input value={itemForm.name} onChange={e => setItemForm({ ...itemForm, name: e.target.value })} required placeholder={itemForm.item_type === 'material' ? 'e.g. Copper Pipe 1/2"' : 'e.g. HVAC Inspection'} data-testid="item-name-input" /></div>
            <div><Label>Description</Label><Textarea value={itemForm.description} onChange={e => setItemForm({ ...itemForm, description: e.target.value })} rows={2} placeholder="Optional" data-testid="item-desc-input" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Price ($)</Label><Input type="number" step="0.01" min="0" value={itemForm.final_price} onChange={e => setItemForm({ ...itemForm, final_price: e.target.value })} data-testid="item-price-input" /></div>
              <div><Label>Cost ($)</Label><Input type="number" step="0.01" min="0" value={itemForm.base_cost} onChange={e => setItemForm({ ...itemForm, base_cost: e.target.value })} placeholder="Optional" data-testid="item-cost-input" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>{itemForm.item_type === 'material' ? 'SKU' : 'Task Code'}</Label><Input value={itemForm.item_type === 'material' ? itemForm.sku : itemForm.task_code} onChange={e => setItemForm({ ...itemForm, [itemForm.item_type === 'material' ? 'sku' : 'task_code']: e.target.value })} placeholder="Optional" data-testid="item-code-input" /></div>
              <div><Label>Unit</Label><Select value={itemForm.unit} onValueChange={v => setItemForm({ ...itemForm, unit: v })}><SelectTrigger data-testid="item-unit-select"><SelectValue /></SelectTrigger><SelectContent>{UNIT_OPTIONS.map(u => <SelectItem key={u.value} value={u.value}>{u.label}</SelectItem>)}</SelectContent></Select></div>
            </div>
            <div className="flex items-center justify-between py-1"><Label className="text-sm">Taxable</Label><Switch checked={itemForm.taxable} onCheckedChange={v => setItemForm({ ...itemForm, taxable: v })} data-testid="item-taxable-switch" /></div>
            {itemForm.item_type === 'service' && <div><Label>Duration (min)</Label><Input type="number" min="0" value={itemForm.default_duration_minutes} onChange={e => setItemForm({ ...itemForm, default_duration_minutes: e.target.value })} placeholder="e.g. 60" data-testid="item-duration-input" /></div>}
          </form>
        </AppDrawer>

        {/* Import Drawer */}
        <AppDrawer open={importDrawerOpen} onOpenChange={(o) => { setImportDrawerOpen(o); if (!o) setImportResult(null); }} title="Import Price Book" description="Upload a CSV file.">
          <div className="space-y-5" data-testid="import-drawer">
            <div className="rounded-md border bg-muted/30 p-3 space-y-2">
              <p className="text-xs font-medium flex items-center gap-1.5"><FileSpreadsheet className="w-3.5 h-3.5" /> Templates</p>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={() => handleExport('items')} disabled={exporting}><Download className="w-3 h-3 mr-1" /> Items</Button>
                <Button variant="outline" size="sm" onClick={() => handleExport('components')} disabled={exporting}><Download className="w-3 h-3 mr-1" /> Components</Button>
                <Button variant="outline" size="sm" onClick={() => handleExport('categories')} disabled={exporting}><Download className="w-3 h-3 mr-1" /> Categories</Button>
              </div>
            </div>
            <div>
              <Label className="text-xs text-muted-foreground mb-1.5 block">Upload CSV</Label>
              <Input ref={fileInputRef} type="file" accept=".csv,text/csv" onChange={e => handleImport(e.target.files?.[0])} disabled={importing} data-testid="import-file-input" className="cursor-pointer" />
              {importing && <div className="flex items-center gap-2 mt-2 text-sm text-muted-foreground"><Loader2 className="w-4 h-4 animate-spin" /> Uploading...</div>}
            </div>
            {importResult?.error && <div className="rounded-md border border-destructive/50 bg-destructive/5 p-3"><p className="text-sm font-medium text-destructive flex items-center gap-1.5"><AlertCircle className="w-4 h-4" /> Import Failed</p><p className="text-xs text-muted-foreground mt-1">{importResult.error}</p></div>}
          </div>
        </AppDrawer>
      </div>

      {/* Mobile FAB */}
      {canEdit && (
        <MobileFABContainer data-testid="mobile-fab-pricebook">
          <MobileFABMenu open={fabOpen} onClose={() => setFabOpen(false)}>
            <MobileFABMenuItem onClick={() => { setFabOpen(false); resetItemForm(); setItemDrawerOpen(true); }}><Plus className="w-4 h-4 mr-2" /> Add Item</MobileFABMenuItem>
            <MobileFABMenuItem onClick={() => { setFabOpen(false); setEditingCat(null); setCatForm({ name: '', description: '', industry_id: '' }); setCatImageFile(null); setCatImagePreview(null); setCatDrawerOpen(true); }}><FolderOpen className="w-4 h-4 mr-2" /> New Category</MobileFABMenuItem>
            <MobileFABMenuItem onClick={() => { setFabOpen(false); handleExport('items'); }}><Download className="w-4 h-4 mr-2" /> Export</MobileFABMenuItem>
          </MobileFABMenu>
          <button onClick={() => setFabOpen(!fabOpen)} className="w-14 h-14 bg-primary text-primary-foreground rounded-full shadow-lg flex items-center justify-center hover:bg-primary/90 transition-all active:scale-95" data-testid="mobile-fab-pricebook-trigger"><Plus className={`w-5 h-5 transition-transform ${fabOpen ? 'rotate-45' : ''}`} strokeWidth={2.5} /></button>
        </MobileFABContainer>
      )}

      {/* Bulk Price Adjustment Drawer */}
      <BulkAdjustDrawer
        open={bulkAdjustOpen}
        onOpenChange={setBulkAdjustOpen}
        categories={categories}
        onApplied={handleRefreshAll}
      />
    </Layout>
  );
}


// ── Helper: Group categories by industry ─────────────────────────────

function groupCategoriesByIndustry(categories, industries) {
  const industryMap = {};
  industries.forEach(ind => { industryMap[ind.id] = ind; });

  const groups = {};
  const unassigned = [];

  categories.forEach(cat => {
    if (cat.industry_id && industryMap[cat.industry_id]) {
      const ind = industryMap[cat.industry_id];
      if (!groups[ind.id]) groups[ind.id] = { industry: ind, cats: [] };
      groups[ind.id].cats.push(cat);
    } else {
      unassigned.push(cat);
    }
  });

  // Return sorted: industries first (by name), unassigned last
  const sorted = Object.values(groups).sort((a, b) => a.industry.name.localeCompare(b.industry.name));
  if (unassigned.length > 0) sorted.push({ industry: null, cats: unassigned });
  return sorted;
}


// ── Grouped Category Sidebar ─────────────────────────────────────────

function GroupedCategorySidebar({ groups, activeCat, canEdit, onSelect, onEdit, onDelete, onSelectIndustry, loading }) {
  const [collapsed, setCollapsed] = useState({});
  const toggle = (id) => setCollapsed(prev => ({ ...prev, [id]: !prev[id] }));

  if (loading) {
    return <div className="flex justify-center py-6"><Loader2 className="w-4 h-4 animate-spin text-muted-foreground" /></div>;
  }

  if (groups.length === 0) {
    return <p className="text-xs text-muted-foreground text-center py-6">No categories yet</p>;
  }

  return (
    <div className="space-y-2">
      {groups.map((g) => {
        const groupId = g.industry ? g.industry.id : '_unassigned';
        const isCollapsed = !!collapsed[groupId];
        const Icon = g.industry ? (INDUSTRY_ICON_MAP[g.industry.icon] || Wrench) : FolderOpen;
        const colors = g.industry ? (IND_COLORS[g.industry.color_key] || DEFAULT_IND_COLORS) : null;

        return (
          <div key={groupId} data-testid={`cat-group-${groupId}`}>
            {/* Group header — clickable row with industry color accent */}
            <button
              className={`w-full flex items-center gap-2 px-2.5 py-2 rounded-lg transition-colors ${
                g.industry ? 'hover:bg-muted/40' : 'hover:bg-muted/30'
              }`}
              onClick={() => toggle(groupId)}
            >
              {isCollapsed
                ? <ChevronRight className="w-3.5 h-3.5 text-muted-foreground/70 shrink-0" />
                : <ChevronDown className="w-3.5 h-3.5 text-muted-foreground/70 shrink-0" />
              }
              <div className={`w-5 h-5 rounded flex items-center justify-center shrink-0 ${colors ? colors.bg : 'bg-muted/60'}`}>
                <Icon className={`w-3 h-3 ${colors ? colors.icon : 'text-muted-foreground/50'}`} />
              </div>
              <span className="text-xs font-semibold tracking-wide truncate">{g.industry ? g.industry.name : 'Unassigned'}</span>
              <Badge variant="outline" className="text-[9px] ml-auto shrink-0 px-1.5 py-0 border-border/50 text-muted-foreground/60">{g.cats.length}</Badge>
            </button>

            {/* Categories nested under this industry */}
            {!isCollapsed && (
              <div className="ml-4 pl-3 border-l-2 border-border/40 space-y-0.5 mt-0.5 mb-2">
                {g.cats.map(cat => (
                  <CategoryCard
                    key={cat.id} cat={cat} isActive={activeCat === cat.id} canEdit={canEdit}
                    onClick={() => onSelect(cat.id)}
                    onEdit={() => onEdit(cat)}
                    onDelete={() => onDelete(cat.id)}
                    compact
                  />
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}


// ── Image Upload Field ───────────────────────────────────────────────

function ImageUploadField({ currentImage, onFileSelect, onRemove, testId }) {
  const ref = useRef(null);
  const [imgError, setImgError] = useState(false);

  const handleChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImgError(false);
    onFileSelect(file, URL.createObjectURL(file));
  };

  const showImage = currentImage && !imgError;

  return (
    <div>
      <Label className="text-xs text-muted-foreground">Image <span className="font-normal">(optional)</span></Label>
      <div className="mt-1.5 flex items-center gap-3">
        {showImage ? (
          <div className="relative group">
            <img
              src={currentImage}
              alt=""
              className="w-16 h-16 rounded-lg object-cover border border-border/50"
              onError={() => setImgError(true)}
              data-testid={`${testId}-preview`}
            />
            <button
              type="button"
              onClick={() => { onRemove(); setImgError(false); }}
              className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-destructive text-destructive-foreground flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity shadow"
              data-testid={`${testId}-remove`}
            >
              <X className="w-3 h-3" />
            </button>
          </div>
        ) : (
          <button
            type="button"
            onClick={() => ref.current?.click()}
            className="w-16 h-16 rounded-lg border-2 border-dashed border-muted-foreground/20 flex flex-col items-center justify-center gap-1 hover:border-primary/30 hover:bg-muted/20 transition-all"
            data-testid={`${testId}-upload-btn`}
          >
            <Camera className="w-4 h-4 text-muted-foreground/40" />
            <span className="text-[9px] text-muted-foreground/40">Upload</span>
          </button>
        )}
        {showImage && (
          <button type="button" onClick={() => ref.current?.click()} className="text-xs text-primary/70 hover:text-primary transition-colors" data-testid={`${testId}-change-btn`}>Change</button>
        )}
        <input ref={ref} type="file" accept=".jpg,.jpeg,.png,.webp" className="hidden" onChange={handleChange} data-testid={`${testId}-input`} />
      </div>
    </div>
  );
}


// ── Category Card ────────────────────────────────────────────────────

function CategoryCard({ cat, isActive, canEdit, onClick, onEdit, onDelete, compact }) {
  const [imgError, setImgError] = useState(false);
  const imgSrc = cat.image_thumb_url || cat.image_url;
  const hasImage = imgSrc && !imgError;
  const size = compact ? 'w-7 h-7' : 'w-8 h-8';
  const iconSize = compact ? 'w-3 h-3' : 'w-3.5 h-3.5';

  return (
    <button
      className={`w-full text-left rounded-lg transition-all duration-150 group ${compact ? 'px-2 py-1.5' : 'px-2.5 py-2'} ${
        isActive
          ? 'bg-primary/8 ring-1 ring-primary/20 shadow-sm'
          : 'hover:bg-muted/60'
      }`}
      onClick={onClick}
      data-testid={`cat-${cat.id}`}
    >
      <div className="flex items-center gap-2.5">
        {/* Image or neutral fallback icon */}
        {hasImage ? (
          <img
            src={imgSrc}
            alt=""
            className={`${size} rounded-md object-cover shrink-0 border border-border/40`}
            onError={() => setImgError(true)}
          />
        ) : (
          <div className={`${size} rounded-md flex items-center justify-center shrink-0 bg-muted/60 border border-border/30`}>
            <FolderOpen className={`${iconSize} text-muted-foreground/50`} />
          </div>
        )}

        {/* Text */}
        <div className="flex-1 min-w-0">
          <div className={`${compact ? 'text-xs' : 'text-[13px]'} font-medium truncate leading-tight ${isActive ? 'text-primary' : ''}`}>{cat.name}</div>
          <div className="text-[10px] text-muted-foreground/60 leading-tight mt-0.5">{cat.item_count} item{cat.item_count !== 1 ? 's' : ''}</div>
        </div>

        {/* Edit/delete — appear on hover */}
        {canEdit && (
          <div className="flex gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity duration-150 shrink-0">
            <span
              role="button"
              className="w-6 h-6 inline-flex items-center justify-center rounded hover:bg-muted transition-colors cursor-pointer"
              onClick={(e) => { e.stopPropagation(); onEdit(); }}
              data-testid={`edit-cat-${cat.id}`}
            >
              <Edit2 className="w-3 h-3 text-muted-foreground" />
            </span>
            <span
              role="button"
              className="w-6 h-6 inline-flex items-center justify-center rounded hover:bg-destructive/10 transition-colors cursor-pointer"
              onClick={(e) => { e.stopPropagation(); onDelete(); }}
              data-testid={`delete-cat-${cat.id}`}
            >
              <Trash2 className="w-3 h-3 text-destructive/60" />
            </span>
          </div>
        )}
      </div>
    </button>
  );
}


// ── Single Industry Tile (extracted to avoid hooks-in-map violation) ──

function IndustryTileItem({ ind, isActive, canEdit, onSelect, onEdit }) {
  const [imgErr, setImgErr] = useState(false);
  const colors = IND_COLORS[ind.color_key] || DEFAULT_IND_COLORS;
  const Icon = INDUSTRY_ICON_MAP[ind.icon] || Wrench;
  const imgSrc = ind.image_thumb_url || ind.image_url;
  const hasImg = imgSrc && !imgErr;

  return (
    <div className="relative group shrink-0">
      {hasImg ? (
        /* ── Image-based card: background image with gradient overlay ── */
        <button
          className={`relative overflow-hidden rounded-xl border-2 text-left transition-all duration-150 min-w-[140px] ${
            isActive
              ? `${colors.active} ring-2 ring-offset-1 ring-primary/40`
              : `${colors.border} hover:shadow-md hover:-translate-y-px`
          }`}
          onClick={() => onSelect(isActive ? '' : ind.id)}
          data-testid={`industry-tile-${ind.id}`}
        >
          <img
            src={imgSrc}
            alt=""
            className="absolute inset-0 w-full h-full object-cover"
            onError={() => setImgErr(true)}
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/65 via-black/40 to-black/25" />
          <div className="relative z-10 flex items-center gap-2.5 px-3.5 py-2.5">
            <div>
              <div className="text-sm font-bold leading-tight text-white drop-shadow-sm">{ind.name}</div>
              <div className="text-[10px] text-white/90 font-medium drop-shadow-sm">{ind.category_count} cat{ind.category_count !== 1 ? 's' : ''} · {ind.item_count}</div>
            </div>
          </div>
        </button>
      ) : (
        /* ── Default card: icon in colored bg ── */
        <button
          className={`flex items-center gap-2.5 px-3.5 py-2.5 rounded-xl border-2 text-left transition-all duration-150 ${
            isActive ? colors.active : `${colors.border} hover:shadow-sm`
          }`}
          onClick={() => onSelect(isActive ? '' : ind.id)}
          data-testid={`industry-tile-${ind.id}`}
        >
          <div className={`w-9 h-9 rounded-lg ${colors.bg} flex items-center justify-center shrink-0`}>
            <Icon className={`w-4 h-4 ${colors.icon}`} />
          </div>
          <div>
            <div className="text-sm font-bold leading-tight">{ind.name}</div>
            <div className="text-[10px] text-muted-foreground">{ind.category_count} cat{ind.category_count !== 1 ? 's' : ''} · {ind.item_count}</div>
          </div>
        </button>
      )}
      {canEdit && (
        <button
          className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-background border shadow-sm flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
          onClick={(e) => { e.stopPropagation(); onEdit(ind); }}
          data-testid={`edit-industry-${ind.id}`}
        >
          <Edit2 className="w-2.5 h-2.5 text-muted-foreground" />
        </button>
      )}
    </div>
  );
}


// ── Industry Tiles ───────────────────────────────────────────────────

function IndustryTiles({ industries, unassigned, activeIndustry, onSelect, canEdit, onEdit, onAdd }) {
  const totalItems = industries.reduce((acc, i) => acc + (i.item_count || 0), 0) + (unassigned.item_count || 0);

  return (
    <div data-testid="industry-tiles-section">
      <div className="flex items-center gap-2 mb-2.5">
        <LayoutGrid className="w-4 h-4 text-primary" />
        <span className="text-xs font-bold uppercase tracking-wider">Industries</span>
        {canEdit && (
          <button className="ml-auto flex items-center gap-1 text-[11px] text-muted-foreground hover:text-primary transition-colors" onClick={onAdd} data-testid="add-industry-btn">
            <Plus className="w-3 h-3" /> Add
          </button>
        )}
      </div>
      <div className="flex gap-2 overflow-x-auto pb-1.5 scrollbar-thin">
        {/* All tile */}
        <button
          className={`flex items-center gap-2.5 px-3.5 py-2.5 rounded-xl border-2 text-left transition-all duration-150 shrink-0 ${
            !activeIndustry
              ? 'border-primary bg-primary/5 shadow-md shadow-primary/10'
              : 'border-border hover:border-primary/20 hover:bg-muted/30'
          }`}
          onClick={() => onSelect('')}
          data-testid="industry-tile-all"
        >
          <div className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${!activeIndustry ? 'bg-primary/10' : 'bg-muted'}`}>
            <LayoutGrid className={`w-4 h-4 ${!activeIndustry ? 'text-primary' : 'text-muted-foreground'}`} />
          </div>
          <div>
            <div className={`text-sm font-bold leading-tight ${!activeIndustry ? 'text-primary' : ''}`}>All</div>
            <div className="text-[10px] text-muted-foreground">{totalItems} items</div>
          </div>
        </button>

        {/* Industry tiles */}
        {industries.map(ind => (
          <IndustryTileItem
            key={ind.id}
            ind={ind}
            isActive={activeIndustry === ind.id}
            canEdit={canEdit}
            onSelect={onSelect}
            onEdit={onEdit}
          />
        ))}

        {/* Unassigned — subtle dashed style */}
        {unassigned.category_count > 0 && (
          <button
            className={`flex items-center gap-2 px-3 py-2 rounded-xl border text-left transition-all duration-150 shrink-0 ${
              activeIndustry === 'unassigned'
                ? 'border-muted-foreground/30 bg-muted/50 shadow-sm'
                : 'border-dashed border-muted-foreground/15 hover:border-muted-foreground/25'
            }`}
            onClick={() => onSelect(activeIndustry === 'unassigned' ? '' : 'unassigned')}
            data-testid="industry-tile-unassigned"
          >
            <div className="w-7 h-7 rounded-md bg-muted/60 flex items-center justify-center shrink-0">
              <FolderOpen className="w-3.5 h-3.5 text-muted-foreground/40" />
            </div>
            <div>
              <div className="text-xs font-medium text-muted-foreground/70">Unassigned</div>
              <div className="text-[9px] text-muted-foreground/50">{unassigned.category_count}</div>
            </div>
          </button>
        )}
      </div>
    </div>
  );
}


/* ── Bulk Price Adjustment Drawer ───────────────────────────── */
function BulkAdjustDrawer({ open, onOpenChange, categories, onApplied }) {
  const [mode, setMode] = useState('percent');
  const [value, setValue] = useState('');
  const [categoryId, setCategoryId] = useState('');
  const [itemType, setItemType] = useState('');
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [applying, setApplying] = useState(false);

  const handlePreview = async () => {
    if (!value && mode !== 'set_to') { toast.error('Enter an adjustment value'); return; }
    setLoading(true);
    setPreview(null);
    try {
      const { data } = await priceBookAPI.bulkPriceAdjust({
        adjustment_mode: mode,
        adjustment_value: parseFloat(value),
        category_id: categoryId || undefined,
        item_type: itemType || undefined,
        apply: false,
      });
      setPreview(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Preview failed');
    }
    setLoading(false);
  };

  const handleApply = async () => {
    if (!preview || preview.total_adjusted === 0) return;
    setApplying(true);
    try {
      const { data } = await priceBookAPI.bulkPriceAdjust({
        adjustment_mode: mode,
        adjustment_value: parseFloat(value),
        category_id: categoryId || undefined,
        item_type: itemType || undefined,
        apply: true,
      });
      toast.success(`Updated ${data.total_adjusted} item prices`);
      onApplied?.();
      onOpenChange(false);
      setPreview(null);
      setValue('');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Apply failed');
    }
    setApplying(false);
  };

  const reset = () => { setPreview(null); setValue(''); setCategoryId(''); setItemType(''); setMode('percent'); };

  return (
    <AppDrawer
      open={open}
      onOpenChange={(v) => { if (!v) reset(); onOpenChange(v); }}
      title="Bulk Price Adjustment"
      description="Adjust prices across multiple items. Preview changes before applying."
      footer={
        <>
          <Button variant="outline" onClick={() => { reset(); onOpenChange(false); }}>Cancel</Button>
          {!preview ? (
            <Button onClick={handlePreview} disabled={loading || !value} data-testid="bulk-preview-btn">
              {loading && <Loader2 className="w-4 h-4 mr-1 animate-spin" />} Preview Changes
            </Button>
          ) : (
            <Button onClick={handleApply} disabled={applying || preview.total_adjusted === 0} variant="default" data-testid="bulk-apply-btn">
              {applying && <Loader2 className="w-4 h-4 mr-1 animate-spin" />} Apply to {preview.total_adjusted} Items
            </Button>
          )}
        </>
      }
    >
      <div className="space-y-4 py-2">
        {/* Filters */}
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="text-xs mb-1 block">Category</Label>
            <Select value={categoryId} onValueChange={(v) => { setCategoryId(v === '_all' ? '' : v); setPreview(null); }}>
              <SelectTrigger data-testid="bulk-category-filter"><SelectValue placeholder="All categories" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="_all">All categories</SelectItem>
                {categories.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs mb-1 block">Item Type</Label>
            <Select value={itemType} onValueChange={(v) => { setItemType(v === '_all' ? '' : v); setPreview(null); }}>
              <SelectTrigger data-testid="bulk-type-filter"><SelectValue placeholder="All types" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="_all">All types</SelectItem>
                <SelectItem value="service">Service</SelectItem>
                <SelectItem value="material">Material</SelectItem>
                <SelectItem value="labor">Labor</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Adjustment mode */}
        <div>
          <Label className="text-xs mb-1 block">Adjustment Type</Label>
          <div className="flex gap-1">
            {[
              { key: 'percent', label: '% Change' },
              { key: 'flat', label: '$ Flat' },
              { key: 'set_to', label: 'Set To' },
            ].map(m => (
              <Button key={m.key} size="sm" variant={mode === m.key ? 'default' : 'outline'} className="flex-1 text-xs" onClick={() => { setMode(m.key); setPreview(null); }} data-testid={`bulk-mode-${m.key}`}>
                {m.label}
              </Button>
            ))}
          </div>
        </div>

        {/* Value input */}
        <div>
          <Label className="text-xs mb-1 block">
            {mode === 'percent' ? 'Percentage (e.g. 10 for +10%, -5 for -5%)' : mode === 'flat' ? 'Dollar amount (e.g. 5 for +$5, -3 for -$3)' : 'New price ($)'}
          </Label>
          <Input
            type="number" step="0.01" value={value}
            onChange={(e) => { setValue(e.target.value); setPreview(null); }}
            placeholder={mode === 'percent' ? '10' : mode === 'flat' ? '5.00' : '99.99'}
            data-testid="bulk-value-input"
          />
        </div>

        {/* Preview results */}
        {preview && (
          <div className="space-y-2" data-testid="bulk-preview-results">
            <div className="flex items-center justify-between text-xs text-muted-foreground">
              <span>{preview.total_adjusted} items to update</span>
              {preview.skipped_bundles > 0 && <span className="text-amber-600">{preview.skipped_bundles} bundles skipped</span>}
              {preview.skipped_negative > 0 && <span className="text-red-600">{preview.skipped_negative} skipped (would go negative)</span>}
            </div>
            <div className="max-h-[260px] overflow-y-auto border rounded-md">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="text-xs py-1.5">Item</TableHead>
                    <TableHead className="text-xs py-1.5 text-right">Old Price</TableHead>
                    <TableHead className="text-xs py-1.5 text-right">New Price</TableHead>
                    <TableHead className="text-xs py-1.5 text-right">Change</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {preview.items.slice(0, 100).map(item => {
                    const diff = item.new_price - item.old_price;
                    return (
                      <TableRow key={item.id}>
                        <TableCell className="text-xs py-1.5">
                          <div className="truncate max-w-[200px]">{item.name}</div>
                          {item.category_name && <div className="text-[10px] text-muted-foreground">{item.category_name}</div>}
                        </TableCell>
                        <TableCell className="text-xs py-1.5 text-right font-mono">${item.old_price.toFixed(2)}</TableCell>
                        <TableCell className="text-xs py-1.5 text-right font-mono font-semibold">${item.new_price.toFixed(2)}</TableCell>
                        <TableCell className={`text-xs py-1.5 text-right font-mono ${diff >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                          {diff >= 0 ? '+' : ''}{diff.toFixed(2)}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
              {preview.items.length > 100 && (
                <p className="text-xs text-muted-foreground text-center py-2">Showing first 100 of {preview.items.length} items</p>
              )}
            </div>
          </div>
        )}
      </div>
    </AppDrawer>
  );
}
